import "../../../assets/scss/instance-card-style.scss";
import { Button, Skeleton } from "antd";
import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { SearchOutlined } from "@ant-design/icons";
import { useNavigate } from "react-router-dom";

import { InstanceCard } from "../../components/instanceCard";
import { PaymentModal } from "../../components/PaymentModal";
import { InstanceCardInterface } from "../../utility/interface/instance-card";

import { getInstanceDetail, getInstances } from "../../server/Instances";
import { useAppDispatch, useAppSelector } from "../../store/hooks";
import { useAuth } from "../../utility/AuthContext";
import { useElapsedTime } from "../../hooks/useElapsedTime";
import { chooseInstance } from "../../features/instance/instanceSlice";

export const Instances = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const { isLoggedIn } = useAuth();

  const user = useAppSelector((s: any) => s.user);
  const instanceRedux = useAppSelector((s: any) => s.instance);
  const instanceStatus = useAppSelector((s: any) => s.instanceStatus.id);
  const completedDetails = useAppSelector(
    (s: any) => s.instanceStatus.completedDetails
  );
  const lastCompleted = useAppSelector(
    (s: any) => s.instanceStatus.lastCompleted
  );

  const [loading, setLoading] = useState(false);
  const [detailLoading, setDetailLoading] = useState(false);

  const [instances, setInstances] = useState<InstanceCardInterface[]>([]);
  const [cachedDetails, setCachedDetails] = useState<any[]>([]);

  const [selected, setSelected] = useState<InstanceCardInterface | null>(null);
  const [createdAt, setCreatedAt] = useState<string>("");

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [paymentData, setPaymentData] = useState<any>(undefined);

  const [internalIP, setInternalIP] = useState("");
  const [sshURL, setSSHURL] = useState("");
  const [sshPassword, setSSHPassword] = useState("");
  const [serviceURL, setServiceURL] = useState("");
  const [notReady, setNotReady] = useState(true);
  const selectIdRef = useRef(0);

  const { days, hours, minutes, seconds, diff, calculateCost } =
    useElapsedTime(createdAt);

  useEffect(() => {
    if (isLoggedIn) {
      setLoading(true);
      fetchInstanceList();
    }
  }, [user]);

  const fetchInstanceList = async () => {
    try {
      const { data } = await getInstances();
      setLoading(false);

      if (data?.instances) {
        const list = data.instances.reverse();
        setInstances(list);

        if (list.length === 1) handleSelect(list[0]);
      } else setInstances([]);
    } catch {
      setLoading(false);
      setInstances([]);
    }
  };

  const findReadyStatus = (id: any) =>
    instanceStatus.find((i: any) => String(i.instance_id) === String(id));

  const applyDetail = (instance: any) => {
    setInternalIP(instance.internal_ip);
    setSSHPassword(instance.ssh_password);
    setSSHURL(instance.ssh_url);
    setServiceURL(instance.service_url);

    if (
      instance.created_at &&
      !isNaN(new Date(instance.created_at).getTime())
    ) {
      setCreatedAt(instance.created_at);
    }

    setCachedDetails((prev) => {
      if (
        prev.some(
          (d) => String(d.instance_id) === String(instance.instance_id)
        )
      ) {
        return prev.map((d) =>
          String(d.instance_id) === String(instance.instance_id) ? instance : d
        );
      }
      return [...prev, instance];
    });
    dispatch(chooseInstance(instance));
    setDetailLoading(false);
  };

  useEffect(() => {
    if (!lastCompleted) return;

    const completedId = String(lastCompleted.instance_id);

    setInstances((prev) => {
      const exists = prev.some((i) => String(i.instance_id) === completedId);
      if (exists) {
        return prev.map((i) => {
          if (String(i.instance_id) === completedId) {
            return {
              ...i,
              ...lastCompleted,
              service_url: lastCompleted.service_url,
              ssh_url: lastCompleted.ssh_url,
              ssh_password: lastCompleted.ssh_password,
              internal_ip: lastCompleted.internal_ip,
              created_at: lastCompleted.created_at || i.created_at,
            };
          }
          return i;
        });
      } else {
        fetchInstanceList();
        return prev;
      }
    });

    setCachedDetails((prev) => {
      const exists = prev.some((d) => String(d.instance_id) === completedId);
      if (exists) {
        return prev.map((d) =>
          String(d.instance_id) === completedId ? lastCompleted : d
        );
      }
      return [...prev, lastCompleted];
    });

    if (selected && String(selected.instance_id) === completedId) {
      applyDetail(lastCompleted);
      setNotReady(false);
    }
  }, [lastCompleted]);

  useEffect(() => {
    if (selected) {
      setNotReady(Boolean(findReadyStatus(selected.instance_id)));
    }
  }, [instanceStatus, selected]);

  const handleSelect = async (item: any) => {
    // Toggle deselect
    if (selected?.instance_id === item.instance_id) {
      setSelected(null);
      setDetailLoading(false);
      return;
    }

    const currentSelectId = ++selectIdRef.current;
    setSelected(item);
    setDetailLoading(true);
    setNotReady(Boolean(findReadyStatus(item.instance_id)));

    // Clear stale fields
    setInternalIP("");
    setSSHURL("");
    setSSHPassword("");
    setServiceURL("");
    setCreatedAt("");

    // Yield so React paints the skeleton before we apply data
    await new Promise((r) => setTimeout(r, 0));
    if (currentSelectId !== selectIdRef.current) return;

    const cached =
      cachedDetails.find(
        (d) => String(d.instance_id) === String(item.instance_id)
      ) || completedDetails?.[String(item.instance_id)];
    if (cached) return applyDetail(cached);

    try {
      const { data } = await getInstanceDetail(item.instance_id);
      if (currentSelectId !== selectIdRef.current) return;
      applyDetail(data.instance);
    } catch (err) {
      if (currentSelectId !== selectIdRef.current) return;
      console.error(err);
      setDetailLoading(false);
    }
  };

  const openPayment = () => {
    setPaymentData({});
    setIsModalOpen(true);
  };

  const showDetail = selected && !detailLoading && createdAt !== "";

  if (loading) return <Skeleton active />;

  return (
    <>
      <div className="instances-container-wrapper">
        {instances.length > 0 ? (
          <>
            <div className="instance-card-container">
              {instances.map((item, idx) => (
                <InstanceCard
                  key={idx}
                  {...item}
                  idx={idx}
                  internal_ip={internalIP}
                  ssh_url={sshURL}
                  ssh_password={sshPassword}
                  notready={notReady}
                  secondaryLoading={detailLoading}
                  isSelected={selected?.instance_id === item.instance_id}
                  onSelect={() => handleSelect(item)}
                  payment={paymentData}
                />
              ))}
            </div>

            {selected && (
              <div className="rental-detail-bar">
                <div className="time-cost">
                  <h3>
                    {t("Cost")}: $
                    {!showDetail ? (
                      "--"
                    ) : notReady ? (
                      "--"
                    ) : (
                      <>{calculateCost(diff, selected.price)}</>
                    )}
                  </h3>
                  <span className="elapsed-time">
                    {!showDetail ? (
                      "--"
                    ) : notReady ? (
                      "--"
                    ) : (
                      <>
                        {days !== 0 && `${days} ${t("Days")}`}{" "}
                        {hours !== 0 && `${hours} ${t("hours")}`}{" "}
                        {minutes !== 0 && `${minutes} ${t("Minutes")}`}{" "}
                        {seconds && `${seconds} ${t("Seconds")}`}
                      </>
                    )}
                  </span>
                </div>

                <div className="actions">
                  <Button
                    className={`use-service ${notReady && "not-ready"}`}
                    disabled={notReady || !showDetail}
                    href={serviceURL}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    {t("Use Service")}
                  </Button>

                  <button
                    disabled={notReady || !showDetail}
                    className={`end-rental ${notReady && "not-ready"}`}
                    onClick={openPayment}
                  >
                    {t("End Rental")}
                  </button>
                </div>
              </div>
            )}
          </>
        ) : (
          <div className="instance-card-none">
            <h2>{t("Instances")}</h2>
            <p className="instance-card-none-descript">{t("No Instances")}</p>
            <div className="secondary-btn" onClick={() => navigate("/")}>
              <SearchOutlined /> &nbsp;{t("Explore GPU Options")}
            </div>
            <img
              src="/images/instances/instances_empty.png"
              width="1160px"
              alt="instances"
            />
          </div>
        )}
      </div>

      <PaymentModal
        visible={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        instance={instanceRedux}
        payment={paymentData}
        createdAt={createdAt}
      />
    </>
  );
};
